import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { readAuditSnapshotIndex, getAuditSnapshotPayload } from "../lib/auditHistory";
import { buildRemediationForFinding, formatRemediationLabel, getPrioritySortWeight } from "../lib/remediationPlaybooks";

function safeObj(x) {
  return x && typeof x === "object" && !Array.isArray(x) ? x : {};
}

function safeNumber(x) {
  const v = Number(x);
  return Number.isFinite(v) ? v : null;
}

function safeArray(x) {
  return Array.isArray(x) ? x : [];
}

function getRiskGrade(score, explicitGrade = "") {
  const explicit = String(explicitGrade || "").trim().toUpperCase();
  if (/^[A-E]$/.test(explicit)) return explicit;
  const n = Number(score);
  if (!Number.isFinite(n)) return "—";
  if (n <= 15) return "A";
  if (n <= 30) return "B";
  if (n <= 50) return "C";
  if (n <= 75) return "D";
  return "E";
}

function fmtDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(String(iso));
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString();
}

function guessCurrencySymbolLocal(code) {
  const c = String(code || "").toUpperCase();
  if (c === "EUR") return "€";
  if (c === "GBP") return "£";
  if (c === "USD") return "$";
  if (c === "JPY") return "¥";
  if (c === "CAD") return "C$";
  if (c === "AUD") return "A$";
  return c ? `${c} ` : "$";
}

function fmtMoney(x, sym = "$") {
  const v = Number(x);
  if (!Number.isFinite(v)) return "—";
  return `${sym}${Math.round(v).toLocaleString("en-US")}`;
}

function fmtMoneyWithCode(x, code = "", sym = "$") {
  const money = fmtMoney(x, sym);
  const normalizedCode = String(code || "").trim().toUpperCase();
  if (money === "—") return money;
  return normalizedCode ? `${normalizedCode} ${money}` : money;
}

function sanitizeFilePart(x, fallback) {
  const out = String(x || "")
    .trim()
    .replace(/[^a-z0-9-_]+/gi, "_")
    .replace(/^_+|_+$/g, "");
  return out || fallback;
}

function sanitizeAssessmentSnapshotPayload(payload = {}) {
  const p = safeObj(payload);
  const summary = safeObj(p.summary);
  const assessment = safeObj(p.assessment);
  const meta = safeObj(p.meta);
  const riskExposure = safeObj(p.risk_exposure);
  const headline = safeObj(riskExposure.headline);
  const combinedByCurrency = safeObj(riskExposure.combined_by_currency);
  const perRegime = safeObj(riskExposure.per_regime);

  return {
    entity_name: p.entity_name,
    business_id: p.business_id,
    snapshot_id: p.snapshot_id,
    created_at_iso: p.created_at_iso,
    generated_at: p.generated_at,
    risk_score_pct: p.risk_score_pct,
    compliance_score_pct: p.compliance_score_pct,
    summary: {
      jurisdictions: Array.isArray(summary.jurisdictions) ? summary.jurisdictions : [],
      entity_name: summary.entity_name,
      currency_symbol: summary.currency_symbol,
      created_at_iso: summary.created_at_iso,
    },
    assessment: {
      risk_score_pct: assessment.risk_score_pct,
      risk_grade: assessment.risk_grade,
      severity_score_0_100: assessment.severity_score_0_100,
      control_coverage_pct: assessment.control_coverage_pct,
      compliance_score_pct: assessment.compliance_score_pct,
      failed_count: assessment.failed_count,
      failed_question_ids: Array.isArray(assessment.failed_question_ids) ? assessment.failed_question_ids : [],
      unsure_count: assessment.unsure_count,
      unsure_question_ids: Array.isArray(assessment.unsure_question_ids) ? assessment.unsure_question_ids : [],
      total_questions: assessment.total_questions,
      active_question_ids: Array.isArray(assessment.active_question_ids) ? assessment.active_question_ids : [],
    },
    meta: {
      risk_score_pct: meta.risk_score_pct,
      compliance_score_pct: meta.compliance_score_pct,
    },
    risk_exposure: {
      headline: {
        currency_code: headline.currency_code,
        currency_symbol: headline.currency_symbol,
        combined_structured_worst_case_native: headline.combined_structured_worst_case_native,
        combined_net_native: headline.combined_net_native,
        combined_net_planning_case_native: headline.combined_net_planning_case_native,
        combined_floor_native: headline.combined_floor_native,
      },
      combined_by_currency: combinedByCurrency,
      per_regime: perRegime,
    },
  };
}

function resolvePrimaryRiskScore(payload = {}) {
  const p = safeObj(payload);
  const assessment = safeObj(p?.assessment);
  const meta = safeObj(p?.meta);

  const candidates = [
    assessment?.risk_score_pct,
    assessment?.severity_score_0_100,
    p?.risk_score_pct,
    meta?.risk_score_pct,
    assessment?.compliance_score_pct,
    p?.compliance_score_pct,
    meta?.compliance_score_pct,
  ];

  for (const candidate of candidates) {
    const n = safeNumber(candidate);
    if (n != null) return Math.round(Math.max(0, Math.min(100, n)));
  }

  return null;
}

function resolveLegacyComplianceScore(payload = {}) {
  const p = safeObj(payload);
  const assessment = safeObj(p?.assessment);
  const meta = safeObj(p?.meta);

  const candidates = [
    assessment?.compliance_score_pct,
    p?.compliance_score_pct,
    meta?.compliance_score_pct,
  ];

  for (const candidate of candidates) {
    const n = safeNumber(candidate);
    if (n != null) return Math.round(Math.max(0, Math.min(100, n)));
  }

  return null;
}

function pickCounts(payload) {
  const a = safeObj(payload?.assessment);
  const failed =
    typeof a.failed_count === "number"
      ? a.failed_count
      : Array.isArray(a.failed_question_ids)
        ? a.failed_question_ids.length
        : 0;
  const unsure =
    typeof a.unsure_count === "number"
      ? a.unsure_count
      : Array.isArray(a.unsure_question_ids)
        ? a.unsure_question_ids.length
        : 0;
  const total =
    typeof a.total_questions === "number"
      ? a.total_questions
      : Array.isArray(a.active_question_ids)
        ? a.active_question_ids.length
        : 60;

  return {
    failed,
    unsure,
    total,
    passed: Math.max(0, Number(total) - Number(failed) - Number(unsure)),
  };
}

function ensureCombinedByCurrency(exposure) {
  const rx = safeObj(exposure);
  const headline = safeObj(rx.headline);
  const buckets = safeObj(rx.combined_by_currency);

  if (Object.keys(buckets).length) return buckets;

  const code = String(headline.currency_code || "").toUpperCase();
  if (!code) return {};

  return {
    [code]: {
      combined_structured_worst_case_native:
        headline.combined_structured_worst_case_native ?? null,
      combined_net_native:
        headline.combined_net_native ??
        headline.combined_net_planning_case_native ??
        null,
      combined_floor_native: headline.combined_floor_native ?? null,
      currency_code: code,
      currency_symbol: headline.currency_symbol ?? guessCurrencySymbolLocal(code),
    },
  };
}

function getHeadlineMetrics(payload) {
  const exposure = safeObj(payload?.risk_exposure);
  const headline = safeObj(exposure.headline);
  const buckets = ensureCombinedByCurrency(exposure);

  const headlineCode = String(headline.currency_code || "").toUpperCase();
  const bucketCode = Object.keys(buckets)[0] || headlineCode || "";
  const activeCode = headlineCode || bucketCode || "";
  const activeBucket = safeObj(buckets[activeCode]);
  const symbol =
    activeBucket.currency_symbol ||
    headline.currency_symbol ||
    guessCurrencySymbolLocal(activeCode);

  return {
    currencyCode: activeCode,
    currencySymbol: symbol,
    high:
      safeNumber(headline.combined_structured_worst_case_native) ??
      safeNumber(activeBucket.combined_structured_worst_case_native),
    expected:
      safeNumber(headline.combined_net_native) ??
      safeNumber(headline.combined_net_planning_case_native) ??
      safeNumber(activeBucket.combined_net_native),
    lower:
      safeNumber(headline.combined_floor_native) ??
      safeNumber(activeBucket.combined_floor_native),
    buckets,
    exposure,
  };
}

function computeAuditDelta(currentValue, previousValue) {
  const cur = safeNumber(currentValue);
  const prev = safeNumber(previousValue);
  if (cur == null || prev == null || prev === 0) return null;
  const change = cur - prev;
  const changeAbs = Math.abs(change);
  const pct = Math.round((change / prev) * 100);
  return {
    change,
    changeAbs,
    pct,
    isDecrease: change < 0,
  };
}

function getPreviousSnapshotPayload(currentPayload) {
  const businessId = String(currentPayload?.business_id || "").trim();
  const snapshotId = String(currentPayload?.snapshot_id || "").trim();
  if (!businessId || !snapshotId) return null;
  try {
    const rows = Array.isArray(readAuditSnapshotIndex({ businessId }))
      ? readAuditSnapshotIndex({ businessId })
      : [];
    const currentIdx = rows.findIndex((r) => String(r?.snapshot_id || "") === snapshotId);
    if (currentIdx < 0) return null;
    const prevRow = rows[currentIdx + 1];
    if (!prevRow?.snapshot_id) return null;
    return getAuditSnapshotPayload(String(prevRow.snapshot_id)) || null;
  } catch {
    return null;
  }
}

function getSinceLastAuditComparison(currentPayload) {
  const previousPayload = getPreviousSnapshotPayload(currentPayload);
  if (!previousPayload) return null;

  const currentMetrics = getHeadlineMetrics(currentPayload);
  const previousMetrics = getHeadlineMetrics(previousPayload);

  const high = computeAuditDelta(currentMetrics.high, previousMetrics.high);
  const expected = computeAuditDelta(currentMetrics.expected, previousMetrics.expected);
  const lower = computeAuditDelta(currentMetrics.lower, previousMetrics.lower);

  if (!high && !expected && !lower) return null;

  return {
    previous_created_at_iso: previousPayload?.created_at_iso || null,
    previous_snapshot_id: previousPayload?.snapshot_id || null,
    currencySymbol: currentMetrics.currencySymbol,
    currencyCode: currentMetrics.currencyCode,
    high,
    expected,
    lower,
  };
}

function drawSinceLastAuditStrip(doc, marginX, y, contentWidth, comparison) {
  if (!comparison) return 0;

  const h = 30;
  doc.setFillColor(248, 250, 252);
  doc.roundedRect(marginX, y, contentWidth, h, 10, 10, "F");
  doc.setDrawColor(226, 232, 240);
  doc.roundedRect(marginX, y, contentWidth, h, 10, 10, "S");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(8.5);
  doc.setTextColor(71, 85, 105);
  doc.text("Since Last Audit", marginX + 14, y + 12);

  const rowY = y + 22;
  const segments = [
    { label: "Higher", delta: comparison.high },
    { label: "Expected", delta: comparison.expected },
    { label: "Lower", delta: comparison.lower },
  ];
  const segW = contentWidth / 3;

  segments.forEach((seg, idx) => {
    const d = seg.delta;
    const x = marginX + idx * segW + 14;
    if (!d) return;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8.5);
    doc.setTextColor(71, 85, 105);
    doc.text(seg.label, x, rowY);

    doc.setTextColor(...(d.isDecrease ? [5, 150, 105] : [185, 28, 28]));
    const money = fmtMoneyWithCode(d.changeAbs, comparison.currencyCode, comparison.currencySymbol);
    doc.text(`${money} (${Math.abs(d.pct)}%)`, x + 42, rowY);
  });

  return h;
}

function drawSectionTitle(doc, text, x, y) {
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(71, 85, 105);
  doc.text(String(text || ""), x, y);
}

function drawMetricCard(doc, { x, y, w, h, label, value, accentFill, accentText }) {
  doc.setFillColor(...accentFill);
  doc.roundedRect(x, y, w, h, 12, 12, "F");
  doc.setDrawColor(226, 232, 240);
  doc.roundedRect(x, y, w, h, 12, 12, "S");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(8.5);
  doc.setTextColor(71, 85, 105);
  doc.text(String(label || ""), x + 14, y + 20);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.setTextColor(...accentText);
  doc.text(String(value || "—"), x + 14, y + 46);
}

function drawDisclosureBox(doc, { x, y, w, title, lines = [], topPad = 20, bottomPad = 18, lineHeight = 11, titleGap = 16 }) {
  const wrapped = lines.map((line) => doc.splitTextToSize(String(line || ""), w - 24));
  const textHeight = wrapped.reduce((sum, block) => sum + block.length * lineHeight, 0);
  const h = topPad + titleGap + textHeight + bottomPad;

  doc.setFillColor(248, 250, 252);
  doc.roundedRect(x, y, w, h, 12, 12, "F");
  doc.setDrawColor(226, 232, 240);
  doc.roundedRect(x, y, w, h, 12, 12, "S");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(71, 85, 105);
  doc.text(String(title || ""), x + 12, y + 18);

  let cursorY = y + topPad + titleGap;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  doc.setTextColor(71, 85, 105);

  wrapped.forEach((block) => {
    doc.text(block, x + 12, cursorY);
    cursorY += block.length * lineHeight;
  });

  return h;
}

export function generateAssessmentSnapshotPdf(payload = {}, options = {}) {
  const cleanPayload = sanitizeAssessmentSnapshotPayload(payload);
  const doc = new jsPDF("p", "pt", "a4");
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const marginX = 34;
  const contentWidth = pageWidth - marginX * 2;
  const footerY = pageHeight - 28;
  const safeBottom = pageHeight - 74;
  const SP = { xs: 6, sm: 10, md: 16, lg: 22, xl: 30 };
  let y = 28;

  const entityName = cleanPayload?.entity_name || "Not specified";
  const createdAt = fmtDateTime(cleanPayload?.created_at_iso || cleanPayload?.generated_at || "");
  const summary = safeObj(cleanPayload?.summary);
  const scopeArr = Array.isArray(summary.jurisdictions) ? summary.jurisdictions : [];
  const scopeText = scopeArr.length ? scopeArr.join(", ") : "—";
  const counts = pickCounts(cleanPayload);
  const complianceScore = resolveLegacyComplianceScore(cleanPayload);
  const riskScore = resolvePrimaryRiskScore(cleanPayload);
  const metrics = getHeadlineMetrics(cleanPayload);
  const nativeCurrencyCode = String(metrics.currencyCode || "").toUpperCase();
  const nativeCurrencySymbol = metrics.currencySymbol || guessCurrencySymbolLocal(nativeCurrencyCode);
  const exposure = metrics.exposure;
  const perRegime = safeObj(exposure?.per_regime);
  const secondaryBuckets = Object.keys(metrics.buckets)
    .filter((code) => String(code).toUpperCase() !== String(metrics.currencyCode).toUpperCase())
    .map((code) => ({
      code: String(code).toUpperCase(),
      bucket: safeObj(metrics.buckets[code]),
    }));

  const addFooter = () => {
    doc.setDrawColor(226, 232, 240);
    doc.line(marginX, footerY - 10, pageWidth - marginX, footerY - 10);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.setTextColor(148, 163, 184);
    doc.text("AI Fine Protector – Assessment Snapshot", marginX, footerY);
    doc.text("© 2026 Advanced Advisory Services, LLC. All Rights Reserved.", marginX, footerY + 10);
    doc.text("Page 1 of 1", pageWidth - marginX, footerY + 10, { align: "right" });
  };

  doc.setFillColor(15, 23, 42);
  doc.roundedRect(marginX, y, contentWidth, 66, 16, 16, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.text("AI Fine Protector", marginX + 18, y + 20);
  doc.setFontSize(18);
  doc.text("Assessment Audit Snapshot", marginX + 18, y + 44);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  doc.text("Locked snapshot record at assessment completion.", pageWidth - marginX - 18, y + 44, { align: "right" });
  y += 80;

  autoTable(doc, {
    startY: y,
    theme: "grid",
    body: [
      ["Entity", entityName, "Date", createdAt],
      ["Scope", scopeText, "Record Status", "Locked Snapshot"],
      ["Currency Basis", "Native Snapshot Currency", "Currency Code", nativeCurrencyCode || "—"],
    ],
    styles: {
      fontSize: 8,
      cellPadding: 6,
      lineColor: [226, 232, 240],
      lineWidth: 1,
      textColor: [15, 23, 42],
      overflow: "linebreak",
      valign: "middle",
    },
    columnStyles: {
      0: { fontStyle: "bold", fillColor: [248, 250, 252], cellWidth: 82 },
      1: { cellWidth: 188 },
      2: { fontStyle: "bold", fillColor: [248, 250, 252], cellWidth: 92 },
      3: { cellWidth: 166 },
    },
    margin: { left: marginX, right: marginX },
  });
  y = doc.lastAutoTable.finalY + SP.md;

  doc.setFillColor(248, 250, 252);
  doc.roundedRect(marginX, y, contentWidth, 28, 10, 10, "F");
  doc.setDrawColor(226, 232, 240);
  doc.roundedRect(marginX, y, contentWidth, 28, 10, 10, "S");
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.8);
  doc.setTextColor(71, 85, 105);
  doc.text(
    `Currency presentation basis: ${nativeCurrencyCode || "—"}. Native snapshot values are locked; platform display views may differ when FX display conversion is enabled.`,
    marginX + 10,
    y + 17,
  );
  y += 40;

  const gap = 10;
  const cardW = (contentWidth - gap * 2) / 3;
  const cardH = 64;
  drawMetricCard(doc, {
    x: marginX,
    y,
    w: cardW,
    h: cardH,
    label: "High",
    value: fmtMoneyWithCode(metrics.high, nativeCurrencyCode, nativeCurrencySymbol),
    accentFill: [254, 242, 242],
    accentText: [185, 28, 28],
  });
  drawMetricCard(doc, {
    x: marginX + cardW + gap,
    y,
    w: cardW,
    h: cardH,
    label: "Expected",
    value: fmtMoneyWithCode(metrics.expected, nativeCurrencyCode, nativeCurrencySymbol),
    accentFill: [239, 246, 255],
    accentText: [29, 78, 216],
  });
  drawMetricCard(doc, {
    x: marginX + (cardW + gap) * 2,
    y,
    w: cardW,
    h: cardH,
    label: "Low",
    value: fmtMoneyWithCode(metrics.lower, nativeCurrencyCode, nativeCurrencySymbol),
    accentFill: [236, 253, 245],
    accentText: [5, 150, 105],
  });
  y += cardH + SP.lg;

  autoTable(doc, {
    startY: y,
    theme: "grid",
    head: [["Metric", "Value", "Metric", "Value"]],
    body: [
      ["Risk Score", riskScore == null ? "—" : `${Math.round(riskScore)}`, "Risk Grade", getRiskGrade(riskScore, cleanPayload?.assessment?.risk_grade)],
      ["Control Coverage", complianceScore == null ? "—" : `${Math.round(complianceScore)}%`, "Failed Controls", String(counts.failed)],
      ["Unsure Controls", String(counts.unsure), "Passed Controls", String(counts.passed)],
    ],
    headStyles: {
      fillColor: [15, 23, 42],
      textColor: [255, 255, 255],
      fontStyle: "bold",
      fontSize: 7.8,
    },
    styles: {
      fontSize: 7.8,
      cellPadding: 6,
      lineColor: [226, 232, 240],
      lineWidth: 1,
      textColor: [15, 23, 42],
      valign: "middle",
    },
    columnStyles: {
      0: { fontStyle: "bold", fillColor: [248, 250, 252], cellWidth: 94 },
      1: { cellWidth: 86 },
      2: { fontStyle: "bold", fillColor: [248, 250, 252], cellWidth: 94 },
      3: { cellWidth: 86 },
    },
    margin: { left: marginX, right: marginX },
  });
  y = doc.lastAutoTable.finalY + SP.lg;

  drawSectionTitle(doc, "Exposure Breakdown", marginX, y);
  y += 10;
  const exposureRows = Object.keys(perRegime).map((key) => {
    const r = safeObj(perRegime[key]);
    const code = String(r.currency_code || "").toUpperCase();
    const sym = r.currency_symbol || guessCurrencySymbolLocal(code);
    return [
      r.display_name || key,
      code || "—",
      fmtMoney(r.statutory_ceiling_native, sym),
      fmtMoney(r.structured_worst_case_native, sym),
      fmtMoney(r.net_exposure_native ?? r.expected_exposure_native ?? r.planning_case_native, sym),
      fmtMoney(r.floor_native, sym),
    ];
  });

  autoTable(doc, {
    startY: y,
    theme: "grid",
    head: [["Regime", "CCY", "Cap", "High", "Expected", "Low"]],
    body: exposureRows.length ? exposureRows : [["No stored regime breakdown", "—", "—", "—", "—", "—"]],
    headStyles: {
      fillColor: [15, 23, 42],
      textColor: [255, 255, 255],
      fontStyle: "bold",
      fontSize: 7.8,
    },
    styles: {
      fontSize: 7.6,
      cellPadding: 5,
      lineColor: [226, 232, 240],
      lineWidth: 1,
      textColor: [15, 23, 42],
      overflow: "linebreak",
      valign: "middle",
    },
    columnStyles: {
      0: { cellWidth: 120, fontStyle: "bold" },
      1: { cellWidth: 42 },
      2: { cellWidth: 88 },
      3: { cellWidth: 88 },
      4: { cellWidth: 88 },
      5: { cellWidth: 88 },
    },
    margin: { left: marginX, right: marginX },
  });
  y = doc.lastAutoTable.finalY + SP.lg;

  if (secondaryBuckets.length) {
    drawSectionTitle(doc, "Secondary Currency Totals", marginX, y);
    y += 10;
    autoTable(doc, {
      startY: y,
      theme: "grid",
      head: [["Currency", "High", "Expected", "Low"]],
      body: secondaryBuckets.map(({ code, bucket }) => {
        const sym = bucket.currency_symbol || guessCurrencySymbolLocal(code);
        return [
          `${code} (native)`,
          fmtMoney(bucket.combined_structured_worst_case_native, sym),
          fmtMoney(bucket.combined_net_native, sym),
          fmtMoney(bucket.combined_floor_native, sym),
        ];
      }),
      headStyles: {
        fillColor: [15, 23, 42],
        textColor: [255, 255, 255],
        fontStyle: "bold",
        fontSize: 7.8,
      },
      styles: {
        fontSize: 7.6,
        cellPadding: 5,
        lineColor: [226, 232, 240],
        lineWidth: 1,
        textColor: [15, 23, 42],
        valign: "middle",
      },
      columnStyles: {
        0: { cellWidth: 118, fontStyle: "bold" },
        1: { cellWidth: 126 },
        2: { cellWidth: 126 },
        3: { cellWidth: 126 },
      },
      margin: { left: marginX, right: marginX },
    });
    y = doc.lastAutoTable.finalY + SP.md;
  }

  const disclosureLines = [
    "AI-generated estimate of potential regulatory exposure based on assessment responses and the AI Fine Protector modeling framework.",
    "Planning estimate only; not legal advice. Actual outcomes depend on facts, jurisdiction, enforcement discretion, remediation, cooperation, and governance maturity.",
    "Locked snapshot record. Later edits or remediation activity do not change this PDF.",
  ];
  const disclosureWrapped = disclosureLines.map((line) => doc.splitTextToSize(String(line || ""), contentWidth - 24));
  const disclosureTextHeight = disclosureWrapped.reduce((sum, block) => sum + block.length * 11, 0);
  const disclosureHeight = 20 + 16 + disclosureTextHeight + 18;

  const basisPreviewHeight = 20 + 16 + (doc.splitTextToSize("Model basis currency.", contentWidth - 24).length * 11 * 3) + 18;
  const remainingBeforeDisclosure = Math.max(0, safeBottom - (y + basisPreviewHeight + disclosureHeight));
  const gapAfterSecondary = Math.min(28, Math.max(14, Math.round(remainingBeforeDisclosure * 0.38)));
  const gapBetweenBoxes = Math.min(32, Math.max(16, Math.round(remainingBeforeDisclosure * 0.62)));

  y += gapAfterSecondary;

  const basisHeight = drawDisclosureBox(doc, {
    x: marginX,
    y,
    w: contentWidth,
    title: "Locked Snapshot Basis",
    lines: [
      `Model basis currency: ${nativeCurrencyCode || "—"}. Jurisdiction values remain native to their stored regime currency.`,
      "Higher, Expected, and Lower figures reflect the saved assessment snapshot at completion time.",
      "Secondary currency totals are included only where the stored snapshot contains that native-currency bucket.",
    ],
    topPad: 20,
    bottomPad: 18,
    lineHeight: 11,
    titleGap: 16,
  });
  y += basisHeight + gapBetweenBoxes;

  let disclosureY = y;
  if (disclosureY + disclosureHeight > safeBottom) {
    disclosureY = safeBottom - disclosureHeight;
  }
  if (disclosureY < y - gapBetweenBoxes) {
    disclosureY = y - gapBetweenBoxes;
  }

  doc.setFillColor(248, 250, 252);
  doc.roundedRect(marginX, disclosureY, contentWidth, disclosureHeight, 12, 12, "F");
  doc.setDrawColor(226, 232, 240);
  doc.roundedRect(marginX, disclosureY, contentWidth, disclosureHeight, 12, 12, "S");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(71, 85, 105);
  doc.text("Planning Disclosure", marginX + 12, disclosureY + 18);

  let disclosureCursorY = disclosureY + 18 + 16;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.4);
  disclosureWrapped.forEach((block) => {
    doc.text(block, marginX + 12, disclosureCursorY);
    disclosureCursorY += block.length * 11;
  });

  addFooter();
  return doc;
}

export function downloadAssessmentSnapshotPdf(payload = {}, options = {}) {
  const cleanPayload = sanitizeAssessmentSnapshotPayload(payload);
  const doc = generateAssessmentSnapshotPdf(cleanPayload, options);
  const entity = sanitizeFilePart(cleanPayload?.entity_name || "Assessment_Audit_Snapshot", "Assessment_Audit_Snapshot");
  const snapshotId = sanitizeFilePart(options?.snapshotId || cleanPayload?.snapshot_id || "snapshot", "snapshot");
  doc.save(`${entity}_${snapshotId}.pdf`);
}

// -----------------------------------------------------------------------------
// Remediation Report Generator (Phase B)
// -----------------------------------------------------------------------------

function safeRemediationRows(remediationMap = {}) {
  return Object.entries(remediationMap || {}).map(([id, row]) => ({
    id: String(id || ""),
    question_id:
      row?.question_id ??
      row?.source_question_id ??
      row?.question_number ??
      null,
    source_question_id:
      row?.source_question_id ??
      row?.question_id ??
      row?.question_number ??
      null,
    question_number:
      row?.question_number ??
      row?.question_id ??
      row?.source_question_id ??
      null,
    status: String(row?.status || "open").toLowerCase(),
    title: String(row?.title || row?.finding_title || row?.question_text || row?.text || "Finding"),
    text: String(row?.text || row?.question_text || ""),
    category: String(row?.category || "—"),
    gravity: Number(row?.gravity || 0) || 0,
    notes: String(row?.notes || ""),
    jurisdictions: Array.isArray(row?.jurisdictions) ? row.jurisdictions : [],
    evidence_links: Array.isArray(row?.evidence_links) ? row.evidence_links : [],
    uploads: Array.isArray(row?.uploads) ? row.uploads : [],
    exposure_high: Number(row?.exposure_high || 0) || 0,
    exposure_expected: Number(row?.exposure_expected || 0) || 0,
    exposure_lower: Number(row?.exposure_lower || 0) || 0,
  }));
}

function stableFindingHash(row = {}) {
  const source = [row?.id, row?.title, row?.text, row?.category].filter(Boolean).join("|").toLowerCase();
  let hash = 0;
  for (let i = 0; i < source.length; i += 1) {
    hash = (hash * 31 + source.charCodeAt(i)) % 1000003;
  }
  return hash;
}

function deriveFindingExposureWeight(row = {}) {
  const gravity = Number(row?.gravity || 0) || 0;
  const text = [row?.title, row?.text, row?.category, row?.remediation_label].filter(Boolean).join(" ").toLowerCase();

  let base = 1;
  if (gravity >= 10) base = 10;
  else if (gravity >= 8) base = 8;
  else if (gravity >= 6) base = 6;
  else if (gravity >= 4) base = 4;
  else if (gravity >= 2) base = 2;

  let multiplier = 1;
  if (/(biometric|social scoring|predictive policing|vulnerable|emotion recognition|deceptive|manipulative)/i.test(text)) {
    multiplier += 0.22;
  }
  if (/(medical|healthcare|critical infrastructure|creditworthiness|insurance|law enforcement|employment|firing|recruitment)/i.test(text)) {
    multiplier += 0.12;
  }
  if (/(privacy|data protection|dpia|ropa|sensitive data)/i.test(text)) {
    multiplier += 0.08;
  }
  if (/(security|adversarial|prompt injection|model weights|cybersecurity)/i.test(text)) {
    multiplier += 0.06;
  }
  if (/(transparency|disclosure|notice|explanation|instructions for deployers)/i.test(text)) {
    multiplier -= 0.05;
  }
  if (/(accountability|governance|inventory|change management|monitoring|recordkeeping|documentation)/i.test(text)) {
    multiplier -= 0.03;
  }

  const jurisdictions = Array.isArray(row?.jurisdictions) ? row.jurisdictions.length : 0;
  multiplier += Math.min(0.04, jurisdictions * 0.01);

  const evidenceSignals =
    (Array.isArray(row?.evidence_links) ? row.evidence_links.length : 0) +
    (Array.isArray(row?.uploads) ? row.uploads.length : 0);
  multiplier -= Math.min(0.03, evidenceSignals * 0.01);

  const hash = stableFindingHash(row);
  const tieBreaker = 1 + (((hash % 17) - 8) / 1000);

  return Math.max(0.1, base * multiplier * tieBreaker);
}

function allocateIntegerTotal(total, weights = []) {
  const safeTotal = Math.max(0, Math.round(Number(total || 0) || 0));
  if (!safeTotal || !Array.isArray(weights) || !weights.length) {
    return (Array.isArray(weights) ? weights : []).map(() => 0);
  }

  const normalized = weights.map((weight) => Math.max(0, Number(weight || 0) || 0));
  const sum = normalized.reduce((acc, value) => acc + value, 0);
  if (!sum) {
    const evenBase = Math.floor(safeTotal / normalized.length);
    const evenRemainder = safeTotal - evenBase * normalized.length;
    return normalized.map((_, index) => evenBase + (index < evenRemainder ? 1 : 0));
  }

  const raw = normalized.map((weight) => (weight / sum) * safeTotal);
  const floors = raw.map((value) => Math.floor(value));
  let remainder = safeTotal - floors.reduce((acc, value) => acc + value, 0);

  const order = raw
    .map((value, index) => ({ index, frac: value - Math.floor(value) }))
    .sort((a, b) => {
      if (b.frac !== a.frac) return b.frac - a.frac;
      return a.index - b.index;
    });

  const output = [...floors];
  let cursor = 0;
  while (remainder > 0 && order.length) {
    output[order[cursor % order.length].index] += 1;
    remainder -= 1;
    cursor += 1;
  }

  return output;
}

function applyDeterministicExposureAllocation(rows = [], snapshot = {}) {
  const list = Array.isArray(rows) ? rows : [];
  const startHigh = Number(snapshot?.payload?.gross_exposure || 0) || 0;
  const startExpected = Number(snapshot?.payload?.net_exposure || 0) || 0;
  const startLower = Number(snapshot?.payload?.weighted_credit_amount || 0) || 0;

  if (!list.length) {
    return {
      rows: [],
      summary: {
        startHigh,
        startExpected,
        startLower,
        reducedHigh: 0,
        reducedExpected: 0,
        reducedLower: 0,
        remainingHigh: Math.max(0, startHigh),
        remainingExpected: Math.max(0, startExpected),
        remainingLower: Math.max(0, startLower),
      },
    };
  }

  const weights = list.map((row) => deriveFindingExposureWeight(row));
  const allocatedHigh = allocateIntegerTotal(startHigh, weights);
  const allocatedExpected = allocateIntegerTotal(startExpected, weights);
  const allocatedLower = allocateIntegerTotal(startLower, weights);

  const allocatedRows = list.map((row, index) => ({
    ...row,
    exposure_high: allocatedHigh[index] ?? 0,
    exposure_expected: allocatedExpected[index] ?? 0,
    exposure_lower: allocatedLower[index] ?? 0,
  }));

  let reducedHigh = 0;
  let reducedExpected = 0;
  let reducedLower = 0;
  allocatedRows.forEach((row) => {
    if (row.status === "closed" || row.status === "mitigated") {
      reducedHigh += row.exposure_high;
      reducedExpected += row.exposure_expected;
      reducedLower += row.exposure_lower;
    }
  });

  return {
    rows: allocatedRows,
    summary: {
      startHigh,
      startExpected,
      startLower,
      reducedHigh,
      reducedExpected,
      reducedLower,
      remainingHigh: Math.max(0, startHigh - reducedHigh),
      remainingExpected: Math.max(0, startExpected - reducedExpected),
      remainingLower: Math.max(0, startLower - reducedLower),
    },
  };
}

function remediationStatusMetrics(rows = []) {
  return {
    open: rows.filter((r) => r.status === "open").length,
    inProgress: rows.filter((r) => r.status === "in_progress").length,
    mitigated: rows.filter((r) => r.status === "mitigated").length,
    closed: rows.filter((r) => r.status === "closed").length,
  };
}

function remediationEvidenceMetrics(rows = []) {
  const findingsWithEvidence = rows.filter((r) => r.evidence_links.length || r.uploads.length).length;
  const findingsMissingEvidence = rows.filter((r) => !r.evidence_links.length && !r.uploads.length).length;
  const totalEvidenceLinks = rows.reduce((a, r) => a + r.evidence_links.length, 0);
  const totalUploads = rows.reduce((a, r) => a + r.uploads.length, 0);
  return { findingsWithEvidence, findingsMissingEvidence, totalEvidenceLinks, totalUploads };
}


function uploadDisplayName(upload = {}) {
  const candidate = [
    upload?.name,
    upload?.file_name,
    upload?.filename,
    upload?.original_name,
    upload?.original_filename,
    upload?.display_name,
    upload?.label,
  ].find((value) => String(value || "").trim());

  if (candidate) return String(candidate).trim();

  const storageKey = String(upload?.storage_key || "");
  if (storageKey) {
    const part = storageKey.split("/").filter(Boolean).pop();
    if (part) return part;
  }

  return "Uploaded file";
}

function uploadAddedAt(upload = {}) {
  const meta = safeObj(upload?.meta);
  const metadata = safeObj(upload?.metadata);
  const candidate = [
    upload?.uploaded_at,
    upload?.uploaded_at_iso,
    upload?.uploadedAt,
    upload?.uploadedAtIso,
    upload?.created_at,
    upload?.createdAt,
    upload?.created_at_iso,
    upload?.createdAtIso,
    upload?.added_at,
    upload?.addedAt,
    upload?.added_at_iso,
    upload?.addedAtIso,
    upload?.date_added,
    upload?.dateAdded,
    upload?.timestamp,
    upload?.updated_at,
    upload?.updatedAt,
    upload?.updated_at_iso,
    upload?.updatedAtIso,
    meta?.uploaded_at,
    meta?.uploaded_at_iso,
    meta?.created_at,
    meta?.created_at_iso,
    metadata?.uploaded_at,
    metadata?.uploaded_at_iso,
    metadata?.created_at,
    metadata?.created_at_iso,
  ].find((value) => String(value || "").trim());

  return candidate ? fmtDateTime(candidate) : "—";
}

function evidenceEntriesForRow(row = {}) {
  const findings = [];
  safeArray(row?.uploads).forEach((upload) => {
    findings.push({
      type: "File upload",
      label: uploadDisplayName(upload),
      addedAt: uploadAddedAt(upload),
      notes: String(upload?.notes || upload?.description || "").trim(),
    });
  });

  safeArray(row?.evidence_links).forEach((link) => {
    findings.push({
      type: "Evidence link",
      label: String(link?.label || link?.url || "Evidence link").trim(),
      addedAt: fmtDateTime(link?.created_at || link?.created_at_iso || link?.added_at || link?.added_at_iso || link?.updated_at || link?.updated_at_iso),
      notes: String(link?.notes || link?.url || "").trim(),
    });
  });

  return findings;
}

function evidenceSupportMetrics(rows = []) {
  const supportedRows = rows.filter((row) => evidenceEntriesForRow(row).length > 0);
  const unsupportedRows = rows.filter((row) => evidenceEntriesForRow(row).length === 0);

  const supportedHigh = supportedRows.reduce((sum, row) => sum + (Number(row?.exposure_high || 0) || 0), 0);
  const unsupportedHigh = unsupportedRows.reduce((sum, row) => sum + (Number(row?.exposure_high || 0) || 0), 0);
  const evidenceItems = rows.reduce((sum, row) => sum + evidenceEntriesForRow(row).length, 0);

  return {
    supportedFindings: supportedRows.length,
    unsupportedFindings: unsupportedRows.length,
    supportedHigh,
    unsupportedHigh,
    evidenceItems,
  };
}


function remediationRowsNeedAllocation(rows = []) {
  if (!Array.isArray(rows) || !rows.length) return false;
  return rows.every((row) => {
    const high = Number(row?.exposure_high || 0) || 0;
    const expected = Number(row?.exposure_expected || 0) || 0;
    const lower = Number(row?.exposure_lower || 0) || 0;
    return high <= 0 && expected <= 0 && lower <= 0;
  });
}

function evidenceExposureSummary(rows = [], snapshot = {}) {
  const rowHigh = rows.reduce((sum, row) => sum + (Number(row?.exposure_high || 0) || 0), 0);
  const rowExpected = rows.reduce((sum, row) => sum + (Number(row?.exposure_expected || 0) || 0), 0);
  const rowLower = rows.reduce((sum, row) => sum + (Number(row?.exposure_lower || 0) || 0), 0);

  const startHigh = Number(snapshot?.payload?.gross_exposure || 0) || rowHigh;
  const startExpected = Number(snapshot?.payload?.net_exposure || 0) || rowExpected;
  const startLower = Number(snapshot?.payload?.weighted_credit_amount || 0) || rowLower;

  return {
    startHigh,
    startExpected,
    startLower,
  };
}

function remediationPriorityRows(rows = []) {
  return [...rows]
    .filter((r) => String(r.status || "").toLowerCase() !== "closed")
    .sort((a, b) => {
      const weightDiff = getPrioritySortWeight(b) - getPrioritySortWeight(a);
      if (weightDiff !== 0) return weightDiff;
      if (b.exposure_high !== a.exposure_high) return b.exposure_high - a.exposure_high;
      return b.gravity - a.gravity;
    })
    .slice(0, 10);
}

function statusLabel(status) {
  const s = String(status || "open").toLowerCase();
  if (s === "in_progress") return "In Progress";
  if (s === "mitigated") return "Mitigated";
  if (s === "closed") return "Closed";
  return "Open";
}

function evidenceLabel(row) {
  const links = Array.isArray(row?.evidence_links) ? row.evidence_links.length : 0;
  const files = Array.isArray(row?.uploads) ? row.uploads.length : 0;
  if (!links && !files) return "No evidence";
  const parts = [];
  if (links) parts.push(`${links} ${links === 1 ? "link" : "links"}`);
  if (files) parts.push(`${files} ${files === 1 ? "file" : "files"}`);
  return parts.join(" / ");
}

function getQuestionNumber(row) {
  const candidates = [
    row?.question_number,
    row?.question_id,
    row?.source_question_id,
  ];

  for (const candidate of candidates) {
    const digits = String(candidate ?? "").match(/\d+/);
    if (digits?.[0]) return digits[0];
  }

  const titleMatch = String(row?.title || "").match(/^Question\s+(\d+)$/i);
  if (titleMatch?.[1]) return titleMatch[1];

  const idMatch = String(row?.id || "").match(/(?:^|_)(\d+)$/);
  if (idMatch?.[1]) return idMatch[1];

  return "";
}

function normalizeStateCode(value) {
  const code = String(value || "").trim().toUpperCase();
  return /^[A-Z]{2}$/.test(code) ? code : "";
}

function uniqueDisplayStrings(items = []) {
  return Array.from(new Set(safeArray(items).map((item) => String(item || "").trim()).filter(Boolean)));
}

function normalizeJurisdictionToken(value) {
  return String(value || "").trim().toUpperCase().replace(/\s+/g, " ");
}

function isUsStatePlaceholder(value) {
  const token = normalizeJurisdictionToken(value).replace(/[_-]/g, " ");
  return token === "US STATE" || token === "US STATES" || token === "US (STATE)";
}

function isUsFederalToken(value) {
  const token = normalizeJurisdictionToken(value).replace(/[_-]/g, " ");
  return token === "US" || token === "US FED" || token === "US FEDERAL" || token === "US (FED)";
}

function isAllJurisdictionsToken(value) {
  const token = normalizeJurisdictionToken(value).replace(/[_-]/g, " ");
  return token === "ALL" || token === "ALL JURISDICTIONS" || token === "GLOBAL";
}

function formatJurisdictionDisplayValue(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const state = normalizeStateCode(raw);
  if (state) return state;
  if (isUsFederalToken(raw)) return "US";
  if (isUsStatePlaceholder(raw)) return "US (STATE)";
  return raw;
}

function expandJurisdictionsForDisplay(values = [], context = {}) {
  const selectedStates = uniqueDisplayStrings(safeArray(context?.selected_states).map(normalizeStateCode).filter(Boolean));
  const summaryScope = uniqueDisplayStrings(context?.scope_jurisdictions);
  const source = uniqueDisplayStrings(values);
  const effective = source.length ? source : summaryScope;
  const output = [];

  const push = (value) => {
    const clean = String(value || "").trim();
    if (!clean || output.includes(clean)) return;
    output.push(clean);
  };

  effective.forEach((item) => {
    if (isAllJurisdictionsToken(item)) {
      const scope = summaryScope.length ? summaryScope : effective;
      scope.forEach((scopeItem) => {
        if (isAllJurisdictionsToken(scopeItem)) return;
        if (isUsStatePlaceholder(scopeItem) && selectedStates.length) {
          selectedStates.forEach(push);
          return;
        }
        push(formatJurisdictionDisplayValue(scopeItem));
      });
      return;
    }

    if (isUsStatePlaceholder(item)) {
      if (selectedStates.length) selectedStates.forEach(push);
      else push("US (STATE)");
      return;
    }

    push(formatJurisdictionDisplayValue(item));
  });

  if (!output.length && selectedStates.length) selectedStates.forEach(push);
  return output;
}

function impactedJurisdictionsLabel(row, context = {}) {
  const values = expandJurisdictionsForDisplay(row?.jurisdictions, context);
  return values.length ? values.join(", ") : "—";
}

function findingDisplay(row) {
  const title = String(row?.title || "Finding").trim();
  const text = String(row?.text || "").trim();
  const questionNumber = getQuestionNumber(row);

  if (questionNumber && text) {
    return `Q${questionNumber}\n${text}`;
  }
  if (questionNumber && title && !/^question\s+\d+$/i.test(title)) {
    return `Q${questionNumber} — ${title}`;
  }
  if (text && text !== title) {
    return `${title}\n${text}`;
  }
  return title;
}

function compactList(items = [], maxItems = 3) {
  return (Array.isArray(items) ? items : [])
    .filter(Boolean)
    .slice(0, maxItems)
    .map((item) => String(item).trim())
    .filter(Boolean)
    .join(" • ");
}

function compactRemediationLabel(row) {
  return formatRemediationLabel(row?.remediation, row?.category);
}

function remediationStatusNarrative(row) {
  const status = String(row?.status || "open").toLowerCase();
  if (status === "closed") {
    return "Closed — preserve closure evidence and validation records.";
  }
  if (status === "mitigated") {
    return "Mitigated — retain residual-risk evidence and monitoring proof.";
  }
  if (status === "in_progress") {
    return "In Progress — complete immediate actions first, then broader control uplift.";
  }
  return "Open — prioritize immediate actions and accountable ownership.";
}

function enrichRemediationRows(rows = [], context = {}) {
  return (Array.isArray(rows) ? rows : []).map((row) => {
    const remediation = buildRemediationForFinding(row, {
      jurisdictions: Array.isArray(row?.jurisdictions) ? row.jurisdictions : [],
      gravity: row?.gravity,
    });

    return {
      ...row,
      remediation,
      remediation_label: remediation?.playbookLabel || row?.category || "Recommended Remediation",
      remediation_priority_text: compactList(remediation?.priorityActions, 3) || remediation?.summary || "—",
      remediation_evidence_text: compactList(remediation?.evidenceFocus, 3) || "—",
      remediation_legal_text: compactList(remediation?.legalChecklist, 3) || "—",
      remediation_overlay_text: String(remediation?.overlaySummaryLine || "").trim(),
      remediation_guidance_text: String(remediation?.guidanceText || "").trim(),
      question_number: getQuestionNumber(row),
      impacted_jurisdictions_label: impactedJurisdictionsLabel(row, context),
    };
  });
}

function addRemediationFooter(doc, titleText) {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const marginX = 42;
  const totalPages = doc.getNumberOfPages();

  for (let i = 1; i <= totalPages; i += 1) {
    doc.setPage(i);
    doc.setDrawColor(226, 232, 240);
    doc.line(marginX, pageHeight - 42, pageWidth - marginX, pageHeight - 42);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(148, 163, 184);
    doc.text(titleText, marginX, pageHeight - 30);
    doc.text("© 2026 Advanced Advisory Services, LLC. All Rights Reserved.", marginX, pageHeight - 18);
    doc.text(`Page ${i} of ${totalPages}`, pageWidth - marginX, pageHeight - 18, { align: "right" });
  }
}

function drawExposureStoryHeader(doc, marginX, y, contentWidth) {
  const levelW = 118;
  const startW = 126;
  const reducedW = 133;
  const remainW = contentWidth - levelW - startW - reducedW;
  const h = 28;

  doc.setFillColor(248, 250, 252);
  doc.roundedRect(marginX, y, contentWidth, h, 8, 8, "F");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(71, 85, 105);
  doc.text("Exposure Level", marginX + 12, y + 18);

  doc.setTextColor(185, 28, 28);
  doc.text("Starting", marginX + levelW + 18, y + 18);

  doc.setTextColor(5, 150, 105);
  doc.text("Reduced By", marginX + levelW + startW + 18, y + 18);

  doc.setTextColor(29, 78, 216);
  doc.text("Remaining", marginX + levelW + startW + reducedW + 18, y + 18);

  return { levelW, startW, reducedW, remainW, h };
}

function drawExposureStoryRow(doc, x, y, widths, label, starting, reduced, remaining) {
  const { levelW, startW, reducedW, remainW } = widths;
  const rowH = 50;

  doc.setDrawColor(226, 232, 240);
  doc.setFillColor(255, 255, 255);
  doc.roundedRect(x, y, levelW + startW + reducedW + remainW, rowH, 8, 8, "FD");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(15, 23, 42);
  doc.text(label, x + 12, y + 16);

  doc.setFontSize(18);
  doc.setTextColor(15, 23, 42);
  doc.text(starting, x + levelW + 18, y + 33);

  doc.setTextColor(5, 150, 105);
  doc.text(reduced, x + levelW + startW + 18, y + 33);

  doc.setTextColor(29, 78, 216);
  doc.text(remaining, x + levelW + startW + reducedW + 18, y + 33);

  return rowH;
}

export function generateRemediationReportPdf(data = {}) {
  const doc = new jsPDF("p", "pt", "a4");
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const marginX = 42;
  const contentWidth = pageWidth - marginX * 2;
  const footerReserve = 58;
  let y = 42;

  const snapshot = safeObj(data?.snapshot);
  const remediationMap = safeObj(data?.remediationMap);
  const entityName = snapshot?.entity_name || "Not specified";
  const snapshotId = snapshot?.snapshot_id || "—";
  const jurisdictionContext = {
    scope_jurisdictions: safeArray(snapshot?.scope_jurisdictions),
    selected_states: safeArray(snapshot?.selected_states),
  };
  const baseRows = enrichRemediationRows(safeRemediationRows(remediationMap), jurisdictionContext);
  const allocation = applyDeterministicExposureAllocation(baseRows, snapshot);
  const rows = allocation.rows;
  const exposure = allocation.summary;
  const status = remediationStatusMetrics(rows);
  const evidence = remediationEvidenceMetrics(rows);
  const priority = remediationPriorityRows(rows);
  const complianceScore = resolveLegacyComplianceScore(snapshot);
  const riskScore = resolvePrimaryRiskScore(snapshot);
  const remediationComparison = safeObj(data?.prior_audit_comparison);

  const ensureSpace = (needed = 100) => {
    if (y + needed <= pageHeight - footerReserve) return;
    doc.addPage();
    y = 42;
  };

  doc.setFillColor(15, 23, 42);
  doc.roundedRect(marginX, y, contentWidth, 72, 18, 18, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text("AI Fine Protector", marginX + 18, y + 22);
  doc.setFontSize(18);
  doc.text("Remediation Report", marginX + 18, y + 42);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text("Based on Locked Snapshot", marginX + 18, y + 58);
  doc.setTextColor(226, 232, 240);
  doc.text(
    "This report reflects the current remediation status associated with the latest Report Snapshot.",
    marginX + 140,
    y + 58
  );
  y += 84;

  autoTable(doc, {
    startY: y,
    theme: "grid",
    body: [
      ["Entity", entityName, "Snapshot ID", String(snapshotId)],
      ["Report Date", fmtDateTime(new Date().toISOString()), "Record Basis", "Locked Snapshot"],
    ],
    styles: {
      fontSize: 9,
      cellPadding: 6,
      lineColor: [226, 232, 240],
      lineWidth: 1,
      textColor: [15, 23, 42],
      overflow: "linebreak",
    },
    columnStyles: {
      0: { fontStyle: "bold", fillColor: [248, 250, 252], cellWidth: 88 },
      1: { cellWidth: 170 },
      2: { fontStyle: "bold", fillColor: [248, 250, 252], cellWidth: 96 },
      3: { cellWidth: 157 },
    },
    margin: { left: marginX, right: marginX },
  });
  y = doc.lastAutoTable.finalY + 12;

  drawSectionTitle(doc, "Exposure Reduction Summary", marginX, y);
  y += 10;
  const widths = drawExposureStoryHeader(doc, marginX, y, contentWidth);
  y += widths.h + 8;
  y += drawExposureStoryRow(
    doc,
    marginX,
    y,
    widths,
    "Higher Exposure",
    fmtMoney(exposure.startHigh),
    fmtMoney(exposure.reducedHigh),
    fmtMoney(exposure.remainingHigh)
  ) + 8;
  y += drawExposureStoryRow(
    doc,
    marginX,
    y,
    widths,
    "Expected Exposure",
    fmtMoney(exposure.startExpected),
    fmtMoney(exposure.reducedExpected),
    fmtMoney(exposure.remainingExpected)
  ) + 8;
  y += drawExposureStoryRow(
    doc,
    marginX,
    y,
    widths,
    "Lower Exposure",
    fmtMoney(exposure.startLower),
    fmtMoney(exposure.reducedLower),
    fmtMoney(exposure.remainingLower)
  ) + 16;

  if (Object.keys(remediationComparison).length) {
    ensureSpace(50);
    const comparisonPayload = {
      currencySymbol: remediationComparison.currencySymbol || "$",
      currencyCode: remediationComparison.currencyCode || "",
      high: remediationComparison.high || null,
      expected: remediationComparison.expected || null,
      lower: remediationComparison.lower || null,
    };
    const sinceH = drawSinceLastAuditStrip(doc, marginX, y, contentWidth, comparisonPayload);
    y += sinceH + 18;
  }

  // ---------- Governance Engine Layer 3 / Remediation Impact (BEGIN) ----------
  const governanceAdjustedExposure = safeObj(snapshot?.governance_adjusted_exposure);
  const layer3 =
    snapshot?.layer_3 && typeof snapshot.layer_3 === "object" && !Array.isArray(snapshot.layer_3)
      ? snapshot.layer_3
      : governanceAdjustedExposure?.layer_3 &&
          typeof governanceAdjustedExposure.layer_3 === "object" &&
          !Array.isArray(governanceAdjustedExposure.layer_3)
        ? governanceAdjustedExposure.layer_3
        : null;

  if (layer3 && governanceAdjustedExposure?.enabled !== false) {
    const layer3Symbol = layer3.currency_symbol || guessCurrencySymbolLocal(layer3.currency_code);

    const fmtPct = (value) => {
      const n = safeNumber(value);
      return n == null ? "—" : `${n}%`;
    };

    const fmtCount = (value) => {
      const n = safeNumber(value);
      return n == null ? "—" : String(n);
    };

    const resolvedCount = safeNumber(layer3.findings_resolved);
    const totalCount = safeNumber(layer3.findings_total);
    const findingsResolvedLabel = `${resolvedCount == null ? "—" : resolvedCount}/${
      totalCount == null ? "—" : totalCount
    }`;

    const layer3Rows = [
      [
        "Exposure Reduction Through Remediation",
        fmtMoney(layer3.exposure_reduction_through_remediation_native, layer3Symbol),
      ],
      ["Remaining Unresolved Exposure", fmtMoney(layer3.remaining_unresolved_exposure_native, layer3Symbol)],
      ["Total Modeled Finding Exposure", fmtMoney(layer3.total_modeled_finding_exposure_native, layer3Symbol)],
      ["Governance Credit Earned", fmtPct(layer3.governance_credit_earned_pct)],
      ["Maximum Governance Credit Available", fmtPct(layer3.maximum_governance_credit_available_pct)],
      ["Remaining Governance Opportunity", fmtPct(layer3.remaining_governance_opportunity_pct)],
      ["Blocked Governance Opportunity", fmtPct(layer3.blocked_governance_opportunity_pct)],
      ["Findings Resolved", findingsResolvedLabel],
      ["Critical Findings Remaining", fmtCount(layer3.critical_findings_remaining)],
      ["Evidence Coverage", fmtPct(layer3.evidence_coverage_pct)],
    ];

    ensureSpace(200);
    drawSectionTitle(doc, "Remediation Impact", marginX, y);
    y += 10;

    autoTable(doc, {
      startY: y,
      theme: "grid",
      head: [["Layer 3 Metric", "Value"]],
      body: layer3Rows,
      headStyles: {
        fillColor: [15, 23, 42],
        textColor: [255, 255, 255],
        fontStyle: "bold",
        fontSize: 9,
      },
      styles: {
        fontSize: 9,
        cellPadding: 6,
        lineColor: [226, 232, 240],
        lineWidth: 1,
        textColor: [15, 23, 42],
        overflow: "linebreak",
      },
      columnStyles: {
        0: { cellWidth: 300, fontStyle: "bold", fillColor: [248, 250, 252] },
        1: { cellWidth: contentWidth - 300 },
      },
      margin: { left: marginX, right: marginX },
    });
    y = doc.lastAutoTable.finalY + 12;

    const layer3Notice = String(
      layer3.public_notice ||
        "Layer 3 measures modeled exposure associated with findings addressed through remediation. It does not predict future fines prevented or eliminate historical exposure."
    );
    const layer3Planning =
      "Remediation may reduce current and future modeled exposure for findings that are addressed and evidenced as governance credit is earned. These figures are conservative planning estimates only; they do not predict, guarantee, or quantify any actual regulatory fine, penalty reduction, or legal outcome.";

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.setTextColor(71, 85, 105);

    const layer3NoticeLines = doc.splitTextToSize(layer3Notice, contentWidth);
    ensureSpace(layer3NoticeLines.length * 10 + 12);
    doc.text(layer3NoticeLines, marginX, y);
    y += layer3NoticeLines.length * 10 + 8;

    const layer3PlanningLines = doc.splitTextToSize(layer3Planning, contentWidth);
    ensureSpace(layer3PlanningLines.length * 10 + 12);
    doc.text(layer3PlanningLines, marginX, y);
    y += layer3PlanningLines.length * 10 + 12;
  }
  // ---------- Governance Engine Layer 3 / Remediation Impact (END) ----------

  ensureSpace(180);
  drawSectionTitle(doc, "Operational Summary", marginX, y);
  y += 10;

  autoTable(doc, {
    startY: y,
    theme: "grid",
    head: [["Remediation Status Metrics", "Count"]],
    body: [
      ["Risk Score", riskScore == null ? "—" : `${Math.round(riskScore)}`],
      ["Risk Grade", getRiskGrade(riskScore, snapshot?.assessment?.risk_grade)],
      ["Control Coverage", complianceScore == null ? "—" : `${Math.round(complianceScore)}%`],
      ["Open", String(status.open)],
      ["In Progress", String(status.inProgress)],
      ["Mitigated", String(status.mitigated)],
      ["Closed", String(status.closed)],
    ],
    headStyles: {
      fillColor: [15, 23, 42],
      textColor: [255, 255, 255],
      fontStyle: "bold",
      fontSize: 9,
    },
    styles: {
      fontSize: 9,
      cellPadding: 6,
      lineColor: [226, 232, 240],
      lineWidth: 1,
      textColor: [15, 23, 42],
    },
    columnStyles: {
      0: { cellWidth: 200, fontStyle: "bold" },
      1: { cellWidth: 56 },
    },
    margin: { left: marginX, right: pageWidth - (marginX + 256) },
  });
  const statusEnd = doc.lastAutoTable.finalY;

  autoTable(doc, {
    startY: y,
    theme: "grid",
    head: [["Evidence Readiness", "Value"]],
    body: [
      ["Findings with Evidence", String(evidence.findingsWithEvidence)],
      ["Findings Missing Evidence", String(evidence.findingsMissingEvidence)],
      ["Total Evidence Links", String(evidence.totalEvidenceLinks)],
      ["Total Uploaded Files", String(evidence.totalUploads)],
    ],
    headStyles: {
      fillColor: [15, 23, 42],
      textColor: [255, 255, 255],
      fontStyle: "bold",
      fontSize: 9,
    },
    styles: {
      fontSize: 9,
      cellPadding: 6,
      lineColor: [226, 232, 240],
      lineWidth: 1,
      textColor: [15, 23, 42],
    },
    columnStyles: {
      0: { cellWidth: 200, fontStyle: "bold" },
      1: { cellWidth: 55 },
    },
    margin: { left: marginX + 267, right: marginX },
  });
  y = Math.max(statusEnd, doc.lastAutoTable.finalY) + 18;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  doc.setTextColor(71, 85, 105);
  const executiveLine = doc.splitTextToSize(
    "Closed and mitigated items represent confirmed exposure reduction. Priority findings below identify the largest remaining unresolved issues and the deterministic remediation actions now recommended for each one.",
    contentWidth
  );
  doc.text(executiveLine, marginX, y);
  y += executiveLine.length * 10;

  doc.addPage();
  y = 42;
  drawSectionTitle(doc, "Priority Unresolved Findings", marginX, y);
  y += 12;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  doc.setTextColor(71, 85, 105);
  doc.text("Findings with the highest remaining potential exposure.", marginX, y);
  y += 16;

  const topDrivers = (priority.length ? priority : rows.slice(0, 3)).slice(0, 3);
  if (topDrivers.length) {
    autoTable(doc, {
      startY: y,
      theme: "grid",
      head: [["Question", "Top 3 Risk Drivers", "Higher Exposure", "Impacted Jurisdictions / States"]],
      body: topDrivers.map((r) => [
        r.question_number ? `Q${r.question_number}` : "—",
        r.title || r.text || "Finding",
        fmtMoney(r.exposure_high),
        r.impacted_jurisdictions_label || "—",
      ]),
      headStyles: {
        fillColor: [15, 23, 42],
        textColor: [255, 255, 255],
        fontStyle: "bold",
        fontSize: 9,
      },
      styles: {
        fontSize: 8.5,
        cellPadding: 6,
        lineColor: [226, 232, 240],
        lineWidth: 1,
        textColor: [15, 23, 42],
        overflow: "linebreak",
      },
      columnStyles: {
        0: { cellWidth: 52, fontStyle: "bold" },
        1: { cellWidth: 235, fontStyle: "bold" },
        2: { cellWidth: 95 },
        3: { cellWidth: 124 },
      },
      margin: { left: marginX, right: marginX },
    });
    y = doc.lastAutoTable.finalY + 18;
  }

  autoTable(doc, {
    startY: y,
    theme: "grid",
    head: [["Question", "Finding Summary", "Impacted Jurisdictions / States", "Status", "Gravity", "Higher Exposure"]],
    body: (priority.length ? priority : rows.slice(0, 10)).map((r) => [
      r.question_number ? `Q${r.question_number}` : "—",
      findingDisplay(r),
      r.impacted_jurisdictions_label || "—",
      statusLabel(r.status),
      String(r.gravity || "—"),
      fmtMoney(r.exposure_high),
    ]),
    headStyles: {
      fillColor: [15, 23, 42],
      textColor: [255, 255, 255],
      fontStyle: "bold",
      fontSize: 7.5,
      cellPadding: 5,
    },
    styles: {
      fontSize: 8,
      cellPadding: 5,
      lineColor: [226, 232, 240],
      lineWidth: 1,
      textColor: [15, 23, 42],
      overflow: "linebreak",
      valign: "top",
    },
    columnStyles: {
      0: { cellWidth: 52, fontStyle: "bold" },
      1: { cellWidth: 177, fontStyle: "bold" },
      2: { cellWidth: 118 },
      3: { cellWidth: 50 },
      4: { cellWidth: 40 },
      5: { cellWidth: 75 },
    },
    margin: { left: marginX, right: marginX },
  });
  y = doc.lastAutoTable.finalY + 18;

  drawSectionTitle(doc, "Recommended Remediation for Priority Findings", marginX, y);
  y += 12;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  doc.setTextColor(71, 85, 105);
  doc.text("Deterministic playbook guidance aligned to finding category, gravity, and active regimes.", marginX, y);
  y += 14;

  autoTable(doc, {
    startY: y,
    theme: "grid",
    head: [["Question", "Finding", "Impacted Jurisdictions / States", "Recommended Remediation", "Top Priority Actions"]],
    body: (priority.length ? priority : rows.slice(0, 5)).slice(0, 5).map((r) => [
      r.question_number ? `Q${r.question_number}` : "—",
      findingDisplay(r),
      r.impacted_jurisdictions_label || "—",
      compactRemediationLabel(r),
      r.remediation_priority_text || "—",
    ]),
    headStyles: {
      fillColor: [15, 23, 42],
      textColor: [255, 255, 255],
      fontStyle: "bold",
      fontSize: 7,
      cellPadding: 5,
    },
    styles: {
      fontSize: 7.5,
      cellPadding: 5,
      lineColor: [226, 232, 240],
      lineWidth: 1,
      textColor: [15, 23, 42],
      overflow: "linebreak",
      valign: "top",
    },
    columnStyles: {
      0: { cellWidth: 52, fontStyle: "bold" },
      1: { cellWidth: 115, fontStyle: "bold" },
      2: { cellWidth: 105 },
      3: { cellWidth: 100 },
      4: { cellWidth: 128 },
    },
    margin: { left: marginX, right: marginX },
  });

  doc.addPage();
  y = 42;
  drawSectionTitle(doc, "Detailed Findings Register", marginX, y);
  y += 12;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  doc.setTextColor(71, 85, 105);
  doc.text("Complete remediation register for actionable findings associated with this latest Report Snapshot.", marginX, y);
  y += 12;

  autoTable(doc, {
    startY: y,
    theme: "grid",
    head: [["#", "Question", "Finding Summary", "Impacted Jurisdictions / States", "Playbook", "Status", "Higher", "Evidence"]],
    body: rows.map((r, idx) => [
      String(idx + 1),
      r.question_number ? `Q${r.question_number}` : "—",
      findingDisplay(r),
      r.impacted_jurisdictions_label || "—",
      compactRemediationLabel(r),
      statusLabel(r.status),
      fmtMoney(r.exposure_high),
      evidenceLabel(r),
    ]),
    headStyles: {
      fillColor: [15, 23, 42],
      textColor: [255, 255, 255],
      fontStyle: "bold",
      fontSize: 7,
      cellPadding: 4,
      valign: "middle",
    },
    styles: {
      fontSize: 7,
      cellPadding: 4,
      lineColor: [226, 232, 240],
      lineWidth: 1,
      textColor: [15, 23, 42],
      overflow: "linebreak",
      valign: "top",
    },
    columnStyles: {
      0: { cellWidth: 20, fontStyle: "bold" },
      1: { cellWidth: 50, fontStyle: "bold" },
      2: { cellWidth: 110, fontStyle: "bold" },
      3: { cellWidth: 84 },
      4: { cellWidth: 74 },
      5: { cellWidth: 38 },
      6: { cellWidth: 42 },
      7: { cellWidth: 54 },
    },
    margin: { left: marginX, right: marginX },
  });
  y = doc.lastAutoTable.finalY + 20;

  const remediationDetailRows = (priority.length ? priority : rows.filter((r) => r.status !== "closed")).slice(0, 8);
  if (remediationDetailRows.length) {
    ensureSpace(190);
    drawSectionTitle(doc, "Recommended Remediation Detail", marginX, y);
    y += 12;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.setTextColor(71, 85, 105);
    doc.text("The items below align the exported report with the deterministic remediation engine now used in-platform.", marginX, y);
    y += 14;

    autoTable(doc, {
      startY: y,
      theme: "grid",
      head: [["Question / Finding", "Impacted Jurisdictions / States", "Recommended Remediation", "Priority / Status", "Legal / Documentation Checklist"]],
      body: remediationDetailRows.map((r) => [
        [r.question_number ? `Q${r.question_number}` : "", findingDisplay(r)].filter(Boolean).join("\n"),
        r.impacted_jurisdictions_label || "—",
        [compactRemediationLabel(r), r.remediation_overlay_text].filter(Boolean).join("\n"),
        [remediationStatusNarrative(r), r.remediation_priority_text || "—"].filter(Boolean).join("\n"),
        r.remediation_legal_text || "—",
      ]),
      headStyles: {
        fillColor: [15, 23, 42],
        textColor: [255, 255, 255],
        fontStyle: "bold",
        fontSize: 7.5,
      },
      styles: {
        fontSize: 7,
        cellPadding: 4.5,
        lineColor: [226, 232, 240],
        lineWidth: 1,
        textColor: [15, 23, 42],
        overflow: "linebreak",
        valign: "top",
      },
      columnStyles: {
        0: { cellWidth: 110, fontStyle: "bold" },
        1: { cellWidth: 95 },
        2: { cellWidth: 108 },
        3: { cellWidth: 120 },
        4: { cellWidth: 101 },
      },
      margin: { left: marginX, right: marginX },
    });
    y = doc.lastAutoTable.finalY + 18;
  }

  ensureSpace(140);
  drawSectionTitle(doc, "Locked Snapshot Disclosure", marginX, y);
  y += 10;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  doc.setTextColor(71, 85, 105);

  const disclosures = [
    "This report is based on a Locked Snapshot and reflects the remediation status recorded at the time this report was generated.",
    "The underlying assessment snapshot does not change when remediation notes, evidence, or statuses are updated later.",
    "Exposure values are planning estimates and do not constitute legal advice.",
  ];
  disclosures.forEach((line) => {
    const lines = doc.splitTextToSize(`• ${line}`, contentWidth);
    doc.text(lines, marginX, y);
    y += lines.length * 10;
  });

  addRemediationFooter(doc, "AI Fine Protector – Remediation Report");
  return doc;
}

export function generateEvidenceDefenseReportPdf(data = {}) {
  const doc = new jsPDF("p", "pt", "a4");
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const marginX = 42;
  const contentWidth = pageWidth - marginX * 2;
  const footerReserve = 58;
  let y = 42;

  const snapshot = safeObj(data?.snapshot);
  const remediationMap = safeObj(data?.remediationMap);
  const entityName = snapshot?.entity_name || "Not specified";
  const snapshotId = snapshot?.snapshot_id || "—";
  const jurisdictionContext = {
    scope_jurisdictions: safeArray(snapshot?.scope_jurisdictions),
    selected_states: safeArray(snapshot?.selected_states),
  };
  const baseRows = enrichRemediationRows(safeRemediationRows(remediationMap), jurisdictionContext);
  const rows = remediationRowsNeedAllocation(baseRows)
    ? applyDeterministicExposureAllocation(baseRows, snapshot).rows
    : baseRows;
  const exposure = evidenceExposureSummary(rows, snapshot);
  const status = remediationStatusMetrics(rows);
  const evidence = remediationEvidenceMetrics(rows);
  const support = evidenceSupportMetrics(rows);
  const summaryLeftWidth = 242;
  const summaryGap = 18;
  const summaryRightWidth = contentWidth - summaryLeftWidth - summaryGap;

  const ensureSpace = (needed = 100) => {
    if (y + needed <= pageHeight - footerReserve) return;
    doc.addPage();
    y = 42;
  };

  const findingTextOnly = (row = {}) => {
    const display = findingDisplay(row);
    const parts = String(display || "").split("\n");
    if (parts.length > 1 && /^Q\d+$/i.test(String(parts[0] || "").trim())) {
      return parts.slice(1).join("\n").trim() || display;
    }
    return String(display || "").replace(/^Q\d+\s*[—-]\s*/i, "").trim() || display;
  };

  const evidenceStatusLabel = (row = {}) => (evidenceEntriesForRow(row).length ? "Evidence attached" : "No evidence attached");

  const potentialFineExposureText = (row = {}) => {
    const amount = fmtMoney(Math.abs(Number(row?.exposure_high || 0) || 0));
    const currentStatus = String(row?.status || "open").toLowerCase();
    if (currentStatus === "closed") return `-${amount}`;
    return amount;
  };

  const potentialFineExposureColor = (row = {}) => {
    const currentStatus = String(row?.status || "open").toLowerCase();
    if (currentStatus === "closed") return [5, 150, 105];
    if (currentStatus === "mitigated") return [180, 83, 9];
    return [185, 28, 28];
  };

  doc.setFillColor(15, 23, 42);
  doc.roundedRect(marginX, y, contentWidth, 74, 18, 18, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text("AI Fine Protector ™", marginX + 18, y + 22);
  doc.setFontSize(18);
  doc.text("Evidence & Defense Report", marginX + 18, y + 42);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(226, 232, 240);
  const headerSubtitle = doc.splitTextToSize(
    "This report documents your remediation evidence and the exposure it supports across the latest Report Snapshot.",
    220
  );
  doc.text(headerSubtitle, pageWidth - marginX - 18, y + 28, { align: "right" });
  y += 88;

  autoTable(doc, {
    startY: y,
    theme: "grid",
    body: [
      ["Entity", entityName, "Snapshot ID", String(snapshotId)],
      ["Report Date", fmtDateTime(new Date().toISOString()), "Record Basis", "Locked Snapshot"],
    ],
    styles: {
      fontSize: 9,
      cellPadding: 6,
      lineColor: [226, 232, 240],
      lineWidth: 1,
      textColor: [15, 23, 42],
      overflow: "linebreak",
    },
    columnStyles: {
      0: { fontStyle: "bold", fillColor: [248, 250, 252], cellWidth: 88 },
      1: { cellWidth: 170 },
      2: { fontStyle: "bold", fillColor: [248, 250, 252], cellWidth: 96 },
      3: { cellWidth: 157 },
    },
    margin: { left: marginX, right: marginX },
  });
  y = doc.lastAutoTable.finalY + 20;

  drawSectionTitle(doc, "Defense Summary", marginX, y);
  y += 16;

  autoTable(doc, {
    startY: y,
    theme: "grid",
    head: [["Evidence Coverage & Exposure", "Value"]],
    body: [
      ["Total Actionable Findings", String(rows.length)],
      ["Findings with Evidence", String(evidence.findingsWithEvidence)],
      ["Findings Without Evidence", String(evidence.findingsMissingEvidence)],
      ["Total Evidence Items", String(support.evidenceItems)],
      ["Open Findings", String(status.open)],
      ["Supported Potential Fine Exposure", fmtMoney(support.supportedHigh)],
      ["Unsupported Potential Fine Exposure", fmtMoney(support.unsupportedHigh)],
    ],
    headStyles: {
      fillColor: [15, 23, 42],
      textColor: [255, 255, 255],
      fontStyle: "bold",
      fontSize: 9,
    },
    styles: {
      fontSize: 9,
      cellPadding: 6,
      lineColor: [226, 232, 240],
      lineWidth: 1,
      textColor: [15, 23, 42],
    },
    columnStyles: {
      0: { cellWidth: 160, fontStyle: "bold" },
      1: { cellWidth: 82 },
    },
    margin: { left: marginX, right: pageWidth - (marginX + summaryLeftWidth) },
    didParseCell: (hookData) => {
      if (hookData.section !== "body" || hookData.column.index !== 1) return;
      const label = String(hookData.row.raw?.[0] || "");
      if (label === "Total Actionable Findings") hookData.cell.styles.textColor = [29, 78, 216];
      if (label === "Supported Potential Fine Exposure") hookData.cell.styles.textColor = [5, 150, 105];
      if (label === "Unsupported Potential Fine Exposure") hookData.cell.styles.textColor = [185, 28, 28];
      if (/Findings|Open Findings|Total Evidence Items/.test(label)) hookData.cell.styles.textColor = [71, 85, 105];
      hookData.cell.styles.fontStyle = "bold";
    },
  });
  const summaryEnd = doc.lastAutoTable.finalY;

  autoTable(doc, {
    startY: y,
    theme: "grid",
    head: [["Exposure Context", "Value"]],
    body: [
      ["Potential Fine Exposure Baseline", fmtMoney(exposure.startHigh)],
      ["Supported Findings", String(support.supportedFindings)],
      ["Unsupported Findings", String(support.unsupportedFindings)],
      ["Exposure Linked to Evidence", fmtMoney(support.supportedHigh)],
      ["Exposure Without Evidence", fmtMoney(support.unsupportedHigh)],
    ],
    headStyles: {
      fillColor: [15, 23, 42],
      textColor: [255, 255, 255],
      fontStyle: "bold",
      fontSize: 9,
    },
    styles: {
      fontSize: 9,
      cellPadding: 6,
      lineColor: [226, 232, 240],
      lineWidth: 1,
      textColor: [15, 23, 42],
    },
    columnStyles: {
      0: { cellWidth: 178, fontStyle: "bold" },
      1: { cellWidth: 73 },
    },
    margin: { left: marginX + summaryLeftWidth + summaryGap, right: marginX },
    didParseCell: (hookData) => {
      if (hookData.section !== "body" || hookData.column.index !== 1) return;
      const label = String(hookData.row.raw?.[0] || "");
      if (label === "Potential Fine Exposure Baseline") hookData.cell.styles.textColor = [29, 78, 216];
      if (label === "Exposure Linked to Evidence") hookData.cell.styles.textColor = [5, 150, 105];
      if (label === "Exposure Without Evidence") hookData.cell.styles.textColor = [185, 28, 28];
      if (/Supported Findings|Unsupported Findings/.test(label)) hookData.cell.styles.textColor = [71, 85, 105];
      hookData.cell.styles.fontStyle = "bold";
    },
  });
  y = Math.max(summaryEnd, doc.lastAutoTable.finalY) + 18;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  doc.setTextColor(71, 85, 105);
  const explainer = doc.splitTextToSize(
    "Evidence-backed findings strengthen defensibility, but do not automatically eliminate exposure. This report shows which findings have supporting files or links, when they were added, and which areas of potential fine exposure remain unsupported.",
    contentWidth
  );
  doc.text(explainer, marginX, y);
  y += explainer.length * 10 + 8;

  const unsupportedRows = rows
    .filter((row) => evidenceEntriesForRow(row).length === 0)
    .sort((a, b) => {
      if (b.exposure_high !== a.exposure_high) return b.exposure_high - a.exposure_high;
      return b.gravity - a.gravity;
    })
    .slice(0, 12);

  doc.addPage();
  y = 42;

  if (unsupportedRows.length) {
    drawSectionTitle(doc, "Highest Unsupported Exposure", marginX, y);
    y += 12;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.setTextColor(71, 85, 105);
    doc.text("Priority findings that currently have no supporting evidence attached.", marginX, y);
    y += 12;

    autoTable(doc, {
      startY: y,
      theme: "grid",
      head: [["Finding", "Evidence Status", "Remediation Status", "Playbook", "Potential Fine Exposure", "Impacted Jurisdictions / States"]],
      body: unsupportedRows.map((row) => [
        findingDisplay(row),
        evidenceStatusLabel(row),
        statusLabel(row.status),
        compactRemediationLabel(row),
        potentialFineExposureText(row),
        row.impacted_jurisdictions_label || "—",
      ]),
      headStyles: {
        fillColor: [15, 23, 42],
        textColor: [255, 255, 255],
        fontStyle: "bold",
        fontSize: 7.3,
      },
      styles: {
        fontSize: 7.1,
        cellPadding: 4.5,
        lineColor: [226, 232, 240],
        lineWidth: 1,
        textColor: [15, 23, 42],
        overflow: "linebreak",
        valign: "top",
      },
      columnStyles: {
        0: { cellWidth: 160, fontStyle: "bold" },
        1: { cellWidth: 64 },
        2: { cellWidth: 68 },
        3: { cellWidth: 82 },
        4: { cellWidth: 72 },
        5: { cellWidth: 65 },
      },
      margin: { left: marginX, right: marginX },
      didParseCell: (hookData) => {
        if (hookData.section !== "body" || hookData.column.index !== 4) return;
        const row = unsupportedRows[hookData.row.index] || {};
        hookData.cell.styles.textColor = potentialFineExposureColor(row);
        hookData.cell.styles.fontStyle = "bold";
      },
    });
    y = doc.lastAutoTable.finalY + 20;
  }

  const supportedRows = rows
    .filter((row) => evidenceEntriesForRow(row).length > 0)
    .sort((a, b) => {
      if (b.exposure_high !== a.exposure_high) return b.exposure_high - a.exposure_high;
      return b.gravity - a.gravity;
    })
    .slice(0, 8);

  if (supportedRows.length) {
    ensureSpace(180);
    drawSectionTitle(doc, "Supported Findings Overview", marginX, y);
    y += 12;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.setTextColor(71, 85, 105);
    doc.text("High-impact findings that already have some evidence attached for review and follow-up.", marginX, y);
    y += 14;

    autoTable(doc, {
      startY: y,
      theme: "grid",
      head: [["Question / Finding", "Evidence on File", "Status / Impact", "Recommended Next Step"]],
      body: supportedRows.map((row) => [
        [row.question_number ? `Q${row.question_number}` : "", findingTextOnly(row)].filter(Boolean).join("\n"),
        evidenceEntriesForRow(row).map((entry) => [entry.label, entry.addedAt].filter(Boolean).join(" — ")).join("\n"),
        [statusLabel(row.status), `Potential fine exposure: ${potentialFineExposureText(row)}`].join("\n"),
        row.remediation_evidence_text || row.remediation_priority_text || "Continue building the evidence record and complete ownership follow-up.",
      ]),
      headStyles: {
        fillColor: [15, 23, 42],
        textColor: [255, 255, 255],
        fontStyle: "bold",
        fontSize: 7.5,
      },
      styles: {
        fontSize: 7.1,
        cellPadding: 4.5,
        lineColor: [226, 232, 240],
        lineWidth: 1,
        textColor: [15, 23, 42],
        overflow: "linebreak",
        valign: "top",
      },
      columnStyles: {
        0: { cellWidth: 156, fontStyle: "bold" },
        1: { cellWidth: 150 },
        2: { cellWidth: 82 },
        3: { cellWidth: 123 },
      },
      margin: { left: marginX, right: marginX },
      didParseCell: (hookData) => {
        if (hookData.section !== "body" || hookData.column.index !== 2) return;
        const row = supportedRows[hookData.row.index] || {};
        hookData.cell.styles.textColor = potentialFineExposureColor(row);
      },
    });
    y = doc.lastAutoTable.finalY + 18;
  }

  const evidenceRegisterColorRows = [];
  const evidenceRegisterRows = rows.flatMap((row) => {
    const entries = evidenceEntriesForRow(row);
    if (!entries.length) {
      evidenceRegisterColorRows.push(row);
      return [[
        findingDisplay(row),
        "No evidence",
        "—",
        "—",
        statusLabel(row.status),
        potentialFineExposureText(row),
      ]];
    }
    return entries.map((entry, idx) => {
      evidenceRegisterColorRows.push(row);
      return [
        idx === 0 ? findingDisplay(row) : "↳",
        entry.label || entry.type,
        entry.type,
        entry.addedAt || "—",
        statusLabel(row.status),
        potentialFineExposureText(row),
      ];
    });
  });

  ensureSpace(200);
  drawSectionTitle(doc, "Evidence Register", marginX, y);
  y += 12;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  doc.setTextColor(71, 85, 105);
  doc.text("Files and links currently associated with actionable findings in this latest Report Snapshot.", marginX, y);
  y += 12;

  autoTable(doc, {
    startY: y,
    theme: "grid",
    head: [["Finding", "Evidence", "Type", "Added", "Status", "Potential Fine Exposure"]],
    body: evidenceRegisterRows,
    headStyles: {
      fillColor: [15, 23, 42],
      textColor: [255, 255, 255],
      fontStyle: "bold",
      fontSize: 7.3,
      cellPadding: 4.5,
    },
    styles: {
      fontSize: 7.1,
      cellPadding: 4.5,
      lineColor: [226, 232, 240],
      lineWidth: 1,
      textColor: [15, 23, 42],
      overflow: "linebreak",
      valign: "top",
    },
    columnStyles: {
      0: { cellWidth: 185, fontStyle: "bold" },
      1: { cellWidth: 116 },
      2: { cellWidth: 58 },
      3: { cellWidth: 72 },
      4: { cellWidth: 40 },
      5: { cellWidth: 40 },
    },
    margin: { left: marginX, right: marginX },
    didParseCell: (hookData) => {
      if (hookData.section !== "body" || hookData.column.index !== 5) return;
      const row = evidenceRegisterColorRows[hookData.row.index] || {};
      hookData.cell.styles.textColor = potentialFineExposureColor(row);
      hookData.cell.styles.fontStyle = "bold";
    },
  });
  y = doc.lastAutoTable.finalY + 18;

  ensureSpace(120);
  drawSectionTitle(doc, "Locked Snapshot Disclosure", marginX, y);
  y += 10;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  doc.setTextColor(71, 85, 105);

  const disclosures = [
    "This report is based on a Locked Snapshot and reflects the remediation evidence recorded at the time this report was generated.",
    "Evidence-backed findings strengthen defensibility, but do not by themselves guarantee full exposure reduction unless remediation status is also validated.",
    "Exposure values are planning estimates and do not constitute legal advice.",
  ];
  disclosures.forEach((line) => {
    const lines = doc.splitTextToSize(`• ${line}`, contentWidth);
    doc.text(lines, marginX, y);
    y += lines.length * 10;
  });

  addRemediationFooter(doc, "AI Fine Protector ™ – Evidence & Defense Report");
  return doc;
}
export function downloadEvidenceDefenseReportPdf(data = {}) {
  const doc = generateEvidenceDefenseReportPdf(data);
  doc.save("AI_Fine_Protector_Evidence_and_Defense_Report.pdf");
}

export function downloadRemediationReportPdf(data = {}) {
  const doc = generateRemediationReportPdf(data);
  doc.save("AI_Fine_Protector_Remediation_Report.pdf");
}

export default {
  generateAssessmentSnapshotPdf,
  downloadAssessmentSnapshotPdf,
  generateRemediationReportPdf,
  downloadRemediationReportPdf,
  generateEvidenceDefenseReportPdf,
  downloadEvidenceDefenseReportPdf,
};
